import React, { ReactNode, useMemo } from "react";
import useTranslation from "../../../../hooks/useTranslation";
import { XmlInvoice, XmlParty, XmlPayment } from "./types";

interface Props {
    invoice: XmlInvoice;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function formatters(locale: string) {
    const money = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const price = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
    });
    const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 4 });

    return {
        money: (value: number | null) => (value === null ? "" : money.format(value)),
        price: (value: number | null) => (value === null ? "" : price.format(value)),
        number: (value: number | null) => (value === null ? "" : number.format(value)),
        percent: (value: number | null) => (value === null ? "" : `${number.format(value)} %`),
        date: (value: string | null) => {
            const match = value ? ISO_DATE.exec(value) : null;
            if (!match) return value ?? "";

            const [, year, month, day] = match;
            return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
                locale,
            );
        },
    };
}

function useFormatters() {
    const { locale } = useTranslation();

    return useMemo(() => formatters(locale), [locale]);
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="flex flex-col gap-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                {title}
            </h3>
            {children}
        </section>
    );
}

function Field({ label, value }: { label: string; value: string | null }) {
    if (!value) return null;

    return (
        <div className="flex justify-between gap-4 text-sm">
            <dt className="text-ink-muted">{label}</dt>
            <dd className="text-right text-ink">{value}</dd>
        </div>
    );
}

function PartyCard({ title, party }: { title: string; party: XmlParty }) {
    const { t } = useTranslation();
    const cityLine = [party.postalZone, party.city].filter(Boolean).join(" ");

    return (
        <Section title={title}>
            <div className="flex flex-col gap-1 text-sm text-ink">
                {party.name && <span className="font-medium">{party.name}</span>}
                {party.street && <span>{party.street}</span>}
                {(cityLine || party.country) && (
                    <span>{[cityLine, party.country].filter(Boolean).join(", ")}</span>
                )}
            </div>
            <dl className="flex flex-col gap-1">
                <Field label={t("xml_viewer.party.company_number")} value={party.companyNumber} />
                <Field label={t("xml_viewer.party.vat_number")} value={party.vatNumber} />
            </dl>
        </Section>
    );
}

function PaymentRow({ payment }: { payment: XmlPayment }) {
    const { t, translateWithFallback } = useTranslation();
    const means = payment.meansCode
        ? translateWithFallback(`xml_viewer.payment.means.${payment.meansCode}`, payment.meansCode)
        : null;

    return (
        <dl className="flex flex-col gap-1">
            <Field label={t("xml_viewer.payment.method")} value={means} />
            <Field label={t("xml_viewer.payment.bank_account")} value={payment.bankAccount} />
            <Field label={t("xml_viewer.payment.iban")} value={payment.iban} />
            <Field label={t("xml_viewer.payment.bic")} value={payment.bic} />
        </dl>
    );
}

const CELL = "px-2 py-1.5 align-top";
const NUMERIC_CELL = `${CELL} text-right tabular-nums whitespace-nowrap`;

function LinesTable({ invoice }: Props) {
    const { t } = useTranslation();
    const format = useFormatters();

    if (invoice.lines.length === 0) {
        return <p className="text-sm text-ink-muted">{t("xml_viewer.lines.empty")}</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b border-border text-left text-xs text-ink-muted">
                        <th className={CELL}>{t("xml_viewer.lines.description")}</th>
                        <th className={NUMERIC_CELL}>{t("xml_viewer.lines.quantity")}</th>
                        <th className={NUMERIC_CELL}>{t("xml_viewer.lines.unit_price")}</th>
                        <th className={NUMERIC_CELL}>{t("xml_viewer.lines.vat_rate")}</th>
                        <th className={NUMERIC_CELL}>{t("xml_viewer.lines.total_excl_vat")}</th>
                        <th className={NUMERIC_CELL}>{t("xml_viewer.lines.total_incl_vat")}</th>
                    </tr>
                </thead>
                <tbody>
                    {invoice.lines.map((line, index) => (
                        <tr key={index} className="border-b border-border/60 text-ink">
                            <td className={`${CELL} break-words`}>{line.description}</td>
                            <td className={NUMERIC_CELL}>
                                {[format.number(line.quantity), line.unit]
                                    .filter(Boolean)
                                    .join(" ")}
                            </td>
                            <td className={NUMERIC_CELL}>{format.price(line.unitPrice)}</td>
                            <td className={NUMERIC_CELL}>{format.percent(line.vatRate)}</td>
                            <td className={NUMERIC_CELL}>{format.money(line.totalExclVat)}</td>
                            <td className={NUMERIC_CELL}>{format.money(line.totalInclVat)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function VatRecapTable({ invoice }: Props) {
    const { t } = useTranslation();
    const format = useFormatters();

    return (
        <table className="w-full border-collapse text-sm">
            <thead>
                <tr className="border-b border-border text-xs text-ink-muted">
                    <th className={`${CELL} text-left`}>{t("xml_viewer.vat_recap.rate")}</th>
                    <th className={NUMERIC_CELL}>{t("xml_viewer.vat_recap.base")}</th>
                    <th className={NUMERIC_CELL}>{t("xml_viewer.vat_recap.vat")}</th>
                    <th className={NUMERIC_CELL}>{t("xml_viewer.vat_recap.total")}</th>
                </tr>
            </thead>
            <tbody>
                {invoice.vatRecap.map((row, index) => (
                    <tr key={index} className="border-b border-border/60 text-ink">
                        <td className={CELL}>{format.percent(row.rate)}</td>
                        <td className={NUMERIC_CELL}>{format.money(row.base)}</td>
                        <td className={NUMERIC_CELL}>{format.money(row.vat)}</td>
                        <td className={NUMERIC_CELL}>{format.money(row.total)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function Totals({ invoice }: Props) {
    const { t } = useTranslation();
    const format = useFormatters();
    const { totals, currency } = invoice;
    const withCurrency = (value: number | null) =>
        value === null ? null : [format.money(value), currency].filter(Boolean).join(" ");
    const nonZero = (value: number | null) => (value ? withCurrency(value) : null);
    const adjusted = !!totals.allowances || !!totals.charges;

    return (
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-1">
            {adjusted && (
                <Field
                    label={t("xml_viewer.totals.line_total")}
                    value={withCurrency(totals.lineTotal)}
                />
            )}
            <Field label={t("xml_viewer.totals.allowances")} value={nonZero(totals.allowances)} />
            <Field label={t("xml_viewer.totals.charges")} value={nonZero(totals.charges)} />
            <Field label={t("xml_viewer.totals.excl_vat")} value={withCurrency(totals.exclVat)} />
            <Field label={t("xml_viewer.totals.vat")} value={withCurrency(totals.vat)} />
            <Field label={t("xml_viewer.totals.incl_vat")} value={withCurrency(totals.inclVat)} />
            <Field label={t("xml_viewer.totals.rounding")} value={nonZero(totals.rounding)} />
            <Field
                label={t("xml_viewer.totals.paid_deposits")}
                value={nonZero(totals.paidDeposits)}
            />
            <div className="mt-1 flex justify-between gap-4 border-t border-border pt-2 text-base font-semibold text-ink">
                <dt>{t("xml_viewer.totals.payable")}</dt>
                <dd className="tabular-nums">{withCurrency(totals.payable)}</dd>
            </div>
        </dl>
    );
}

export default function InvoiceView({ invoice }: Props) {
    const { t } = useTranslation();
    const format = useFormatters();

    return (
        <article className="mx-auto flex w-full max-w-4xl flex-col gap-8 rounded-sm bg-surface p-8 text-ink shadow-[0_8px_24px_0_rgb(0_0_0/0.08)]">
            <header className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-accent">
                        {t(`xml_viewer.document_types.${invoice.documentType}`)}
                    </span>
                    <h2 className="text-2xl font-semibold">{invoice.number}</h2>
                </div>
                <dl className="flex min-w-64 flex-col gap-1">
                    <Field
                        label={t("xml_viewer.header.variable_symbol")}
                        value={invoice.variableSymbol}
                    />
                    <Field
                        label={t("xml_viewer.header.issue_date")}
                        value={format.date(invoice.issueDate)}
                    />
                    <Field
                        label={t("xml_viewer.header.tax_point_date")}
                        value={format.date(invoice.taxPointDate)}
                    />
                    <Field
                        label={t("xml_viewer.header.due_date")}
                        value={format.date(invoice.dueDate)}
                    />
                    <Field label={t("xml_viewer.header.currency")} value={invoice.currency} />
                </dl>
            </header>

            <div className="grid gap-8 md:grid-cols-2">
                <PartyCard title={t("xml_viewer.party.supplier")} party={invoice.supplier} />
                <PartyCard title={t("xml_viewer.party.customer")} party={invoice.customer} />
            </div>

            {invoice.payments.length > 0 && (
                <Section title={t("xml_viewer.payment.title")}>
                    <div className="grid gap-4 md:grid-cols-2">
                        {invoice.payments.map((payment, index) => (
                            <PaymentRow key={index} payment={payment} />
                        ))}
                    </div>
                </Section>
            )}

            <Section title={t("xml_viewer.lines.title")}>
                <LinesTable invoice={invoice} />
            </Section>

            {invoice.vatRecap.length > 0 && (
                <Section title={t("xml_viewer.vat_recap.title")}>
                    <VatRecapTable invoice={invoice} />
                </Section>
            )}

            <Section title={t("xml_viewer.totals.title")}>
                <Totals invoice={invoice} />
            </Section>
        </article>
    );
}
